import type { Context, TextMapPropagator } from '@opentelemetry/api';
import { propagation } from '@opentelemetry/api';
import {
  AlwaysOffSampler,
  AlwaysOnSampler,
  ParentBasedSampler,
  type Sampler,
  TraceIdRatioBasedSampler,
} from '@opentelemetry/sdk-trace-web';
import { type Resource, resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import {
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
  ATTR_SERVICE_NAMESPACE,
} from '@opentelemetry/semantic-conventions/incubating';

/** OTEL_TRACES_SAMPLER 未指定時の既定 (dev/stg 相当)。 */
const DEFAULT_TRACES_SAMPLER = 'parentbased_always_on';
/** OTEL_TRACES_SAMPLER_ARG 未指定 / 解釈不能時の既定サンプリング率。 */
const DEFAULT_SAMPLER_RATIO = 1;

const BAGGAGE_ORGANIZATION_ID_KEY = 'organization_id';

/**
 * `OTEL_TRACES_SAMPLER` / `OTEL_TRACES_SAMPLER_ARG` 相当の値から Sampler を構築する
 * (設計 05 §4.1)。未知 / 未指定は `parentbased_always_on` にフォールバックする。
 */
export function resolveSampler(name?: string, arg?: string | number): Sampler {
  const ratio = parseSamplerRatio(arg);
  switch (name && name.length > 0 ? name : DEFAULT_TRACES_SAMPLER) {
    case 'always_on':
      return new AlwaysOnSampler();
    case 'always_off':
      return new AlwaysOffSampler();
    case 'traceidratio':
      return new TraceIdRatioBasedSampler(ratio);
    case 'parentbased_always_off':
      return new ParentBasedSampler({ root: new AlwaysOffSampler() });
    case 'parentbased_traceidratio':
      return new ParentBasedSampler({ root: new TraceIdRatioBasedSampler(ratio) });
    case 'parentbased_always_on':
    default:
      return new ParentBasedSampler({ root: new AlwaysOnSampler() });
  }
}

function parseSamplerRatio(arg?: string | number): number {
  if (arg === undefined || arg === '') {
    return DEFAULT_SAMPLER_RATIO;
  }
  const parsed = typeof arg === 'number' ? arg : Number.parseFloat(arg);
  if (Number.isNaN(parsed)) {
    return DEFAULT_SAMPLER_RATIO;
  }
  return Math.min(1, Math.max(0, parsed));
}

export interface OtlpAuthHeadersOptions {
  /** Auth0 から Bearer トークンを取得する (未 ready 時は undefined を返す想定)。 */
  getToken: () => Promise<string | undefined>;
  /** トークンが揃うまで待機する上限 (ms)。超過時は認証ヘッダ無しで返す。 */
  maxWaitMillis?: number;
  /** 待機中のポーリング間隔 (ms)。 */
  pollIntervalMillis?: number;
  /** テスト差し替え用。 */
  sleep?: (ms: number) => Promise<void>;
  /** テスト差し替え用。 */
  now?: () => number;
}

const DEFAULT_MAX_WAIT_MILLIS = 8000;
const DEFAULT_POLL_INTERVAL_MILLIS = 250;

/**
 * OTLP exporter の `headers` に渡す async factory を生成する (設計 07 §3.1)。
 *
 * telemetry plugin は auth0 より先 (`order: -100`) に走るため、初回 export が
 * auth0 初期化完了前に発火し得る。`@opentelemetry/otlp-exporter-base` の契約で
 * headers factory は **throw してはならない**ため、トークンが揃うまで上限付きで
 * 待機して export を遅延させる (「その回を落とさず auth0 ready 後へずらす」方針)。
 * 上限超過時のみ認証ヘッダ無しで返す (degraded)。
 */
export function createOtlpAuthHeadersProvider(
  options: OtlpAuthHeadersOptions,
): () => Promise<Record<string, string>> {
  const {
    getToken,
    maxWaitMillis = DEFAULT_MAX_WAIT_MILLIS,
    pollIntervalMillis = DEFAULT_POLL_INTERVAL_MILLIS,
    sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
    now = () => Date.now(),
  } = options;

  return async (): Promise<Record<string, string>> => {
    const deadline = now() + maxWaitMillis;
    for (;;) {
      // 逐次ポーリング: トークンが揃うまで順に待機するため並列化はできない。
      // eslint-disable-next-line no-await-in-loop
      const token = await safeGetToken(getToken);
      if (token) {
        return { Authorization: `Bearer ${token}` };
      }
      if (now() >= deadline) {
        return {};
      }
      // eslint-disable-next-line no-await-in-loop
      await sleep(pollIntervalMillis);
    }
  };
}

async function safeGetToken(
  getToken: () => Promise<string | undefined>,
): Promise<string | undefined> {
  try {
    return await getToken();
  } catch {
    return undefined;
  }
}

export interface SilentTokenSource {
  getAccessTokenSilently: () => Promise<string>;
}

/**
 * OTLP export 用の token getter を生成する (レビュー指摘 Minor)。
 *
 * アプリの `useToken()` は refresh token 失効 (`missing_refresh_token` / `invalid_grant`)
 * 時に `navigateTo(LOGOUT)` を誘発するため、BSP のバックグラウンド export タイマから
 * 使うと telemetry が認証リダイレクトに結合してしまう。ここでは `getAccessTokenSilently`
 * を直接呼び、失敗時は undefined を返す (degraded export に落とすだけでログアウト遷移は
 * させない)。auth0 client (`useAuth0()` 等) は Nuxt/Vue context 内で解決する必要があるため
 * 呼び出し側が runWithContext 等で context を張る。
 */
export function createSilentTokenGetter(
  resolveTokenSource: () => SilentTokenSource,
): () => Promise<string | undefined> {
  return async () => {
    try {
      return await resolveTokenSource().getAccessTokenSilently();
    } catch {
      return undefined;
    }
  };
}

export interface TelemetryResourceAttributes {
  serviceName: string;
  serviceVersion?: string;
  deploymentEnvironment?: string;
  serviceNamespace?: string;
}

/** Resource 属性を semconv 定数で構築する (設計 05 §5)。 */
export function buildTelemetryResource(attrs: TelemetryResourceAttributes): Resource {
  const resourceAttributes: Record<string, string> = {
    [ATTR_SERVICE_NAME]: attrs.serviceName,
    [ATTR_SERVICE_NAMESPACE]: attrs.serviceNamespace ?? 'datastore',
  };
  if (attrs.serviceVersion) {
    resourceAttributes[ATTR_SERVICE_VERSION] = attrs.serviceVersion;
  }
  if (attrs.deploymentEnvironment) {
    resourceAttributes[ATTR_DEPLOYMENT_ENVIRONMENT_NAME] = attrs.deploymentEnvironment;
  }
  return resourceFromAttributes(resourceAttributes);
}

/**
 * auto-instrumentation の `propagateTraceHeaderCorsUrls` に渡す許可 URL 正規表現を
 * baseURL 群から導出する (設計 07 §3.1)。
 *
 * **origin ではなく origin + パス接頭辞で一致させる。** SENSYN の API は複数サービスが
 * 1 つのオリジンに同居しており (例: `https://<env>` 配下に domain-service `/api/v2/domain-service`、
 * sensyn-id `/api/v1/sensyn-id`、グローバルナビ `/global-navigation`、Streamap `/api/v1/streamap`)、
 * origin 単位で許可すると **計装していないサービスにも `traceparent` / `baggage` が付く**。
 * 未計装サービスの CORS は これらを `Access-Control-Allow-Headers` に含めていないため
 * preflight が落ちて呼び出し自体が失敗する (グローバルナビで実際に発生した)。
 * 落ちない場合でも baggage の `organization_id` が無関係のサービスへ渡ってしまう。
 *
 * 境界は `(?:$|[/?#])`。判定対象は絶対 URL なので query / hash 付きも一致させる必要があり、
 * かつ `/api/v3-evil` のようなパスセグメント偽装と `https://api.example.com.evil.com` のような
 * ホスト偽装を防ぐ (`resolveOtlpIgnoreUrls` と同じ扱い)。
 *
 * ⚠️ baseURL にパスが無い (裸のオリジン) 場合は origin 単位の許可へ縮退し、同じオリジンに
 * 同居する全サービスへ伝播する。同居サービスがあるオリジンではパス付きで設定すること。
 *
 * 不正 / 未設定 URL は無視し、重複する接頭辞は 1 本にまとめる。
 */
export function resolvePropagateTraceHeaderCorsUrls(baseUrls: (string | undefined)[]): RegExp[] {
  const prefixes = new Set<string>();
  for (const baseUrl of baseUrls) {
    const prefix = toOriginWithPathPrefix(baseUrl);
    if (prefix) {
      prefixes.add(prefix);
    }
  }
  return Array.from(prefixes, (prefix) => new RegExp(`^${escapeRegExp(prefix)}(?:$|[/?#])`));
}

/**
 * OTLP trace の export 先エンドポイントを collector URL から導出する。
 *
 * exporter の送信先と ignoreUrls の除外対象が食い違うと自己増殖ループ (下記) が復活するため、
 * 両者はこの 1 箇所から導出する。不正 / 未設定 URL は undefined。
 *
 * パスの連結は文字列でなく URL オブジェクト上で行う (Copilot 指摘)。collector URL に
 * query / hash が紛れ込んだ設定ミスがあると、単純な文字列連結では
 * `https://collector.test/?x=1/v1/traces` のような不正な送信先になってしまうため
 * (isSecureCollectorUrl は https かどうかしか見ないので弾けない)。OTLP エンドポイントに
 * query / hash は意味を持たないので落とす。
 */
export function resolveOtlpTracesEndpoint(collectorUrl?: string): string | undefined {
  if (!collectorUrl) {
    return undefined;
  }
  try {
    const url = new URL(collectorUrl);
    url.search = '';
    url.hash = '';
    url.pathname = `${url.pathname.replace(/\/+$/, '')}/v1/traces`;
    return url.href;
  } catch {
    return undefined;
  }
}

/**
 * OTLP collector 自身への export 送信を fetch/xhr 計装の対象から除外する ignoreUrls を
 * 導出する (レビュー指摘 Critical)。
 *
 * これを設定しないと collector への OTLP POST 自体が span 化され、次バッチでその span を
 * export → 更に POST が span 化…という自己増殖ループになる (browser OTLP exporter は
 * fetch で送信し、instrumentation-fetch は BatchSpanProcessor の suppressTracing を参照
 * しない。export span は root で parentbased_always_on では常に sampled)。
 *
 * 除外は origin 全体ではなく export 先パス (`/v1/traces`) までに限定する (Copilot 指摘)。
 * collector をアプリ / API と同一 origin にぶら下げた構成 (リバプロ配下等) で、OTLP 以外の
 * 通信まで計装対象から外れて可観測性が失われるのを防ぐ。
 * collector URL 未設定 (ConsoleSpanExporter) 時は空配列 (除外不要)。
 */
export function resolveOtlpIgnoreUrls(collectorUrl?: string): RegExp[] {
  const endpoint = resolveOtlpTracesEndpoint(collectorUrl);
  if (!endpoint) {
    return [];
  }
  return [new RegExp(`^${escapeRegExp(endpoint)}(?:$|[?#])`)];
}

/**
 * collector URL が Auth0 Bearer を安全に送れる有効な https URL かを判定する (Copilot 指摘)。
 *
 * `OTEL_COLLECTOR_URL` は build 時 config 注入で外部から与えられるため、不正 URL だと
 * exporter 構築が throw して計装全体が無効化され、http だと Auth0 Bearer が平文で漏れる。
 * 不正 / 非 https の場合は OTLP を使わない (フォールバック先は
 * resolveTelemetryExporterKind が環境で振り分ける)。
 */
export function isSecureCollectorUrl(url: string): boolean {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

/** span の出力先。`none` は provider を登録せず計装自体を行わないことを表す。 */
export type TelemetryExporterKind = 'otlp' | 'console' | 'none';

/**
 * collector URL と環境から span の出力先を決める。
 *
 * ConsoleSpanExporter へのフォールバックを**ローカル開発限定**にするのが要点 (オーナー指摘)。
 * k8s の OTEL 系 env は kubernetes-manifest#3225 で投入予定であり、それ以前のイメージが
 * stg/prod へ出ると、collector URL 未設定 → Console フォールバックとなって本番ユーザの
 * ブラウザ console に全 span が出力され続ける (エラーにはならないが可視のノイズ +
 * BatchSpanProcessor のオーバーヘッドを払って Tempo には 0 件)。そのため非ローカルでは
 * `none` を返し、呼び出し側は provider を登録せず span 生成も export も行わない。
 * これで env 投入の前後どちらでも安全にデプロイでき、dev 先行 → stg/prod 昇格の順序を守れる。
 *
 * ローカル判定は `DEPLOYMENT_ENVIRONMENT === 'local'`。この値は config.template.json の
 * 既定値であり、Docker では generate-config.sh が全キーをプレースホルダー化して envsubst で
 * env を注入するため、**env 未投入の環境では空文字**になり非ローカル扱いに倒れる。
 *
 * 不正 / 非 https の collector URL も未設定と同じ扱い (ローカルなら console、他は none) に
 * 倒して一貫させる。設定ミスの可視化は呼び出し側の warn が担う。
 */
export function resolveTelemetryExporterKind(
  collectorUrl: string,
  deploymentEnvironment?: string,
): TelemetryExporterKind {
  if (collectorUrl.length > 0 && isSecureCollectorUrl(collectorUrl)) {
    return 'otlp';
  }
  return deploymentEnvironment === 'local' ? 'console' : 'none';
}

/**
 * baseURL を「オリジン + パス接頭辞」へ正規化する。
 *
 * 末尾スラッシュは落とす。axios は `combineURLs` で baseURL 末尾の `/` を畳むため実 URL 側に
 * `//` は現れず、落とさないと `.../api/v3/` 設定時に一致しなくなる。query / hash も
 * 接頭辞としては意味を持たないので捨てる (`resolveOtlpTracesEndpoint` と同じ扱い)。
 */
function toOriginWithPathPrefix(url?: string): string | undefined {
  if (!url) {
    return undefined;
  }
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname.replace(/\/+$/, '')}`;
  } catch {
    return undefined;
  }
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * baggage の発生源 (origination) を担う propagator ラッパ (設計 05 §6 / 07 §3.1)。
 *
 * CompositePropagator に登録した W3CBaggagePropagator を単に積むだけでは、active
 * context に baggage エントリが無いため空のまま伝播する。SPA では全 fetch を包む
 * 永続的な active context を張れないため、inject 直前に `organization_id`
 * (非機密の業務識別子のみ) を context へ set してから委譲する。
 *
 * ⚠️ same-origin リクエストには auto-instrumentation が伝播ヘッダを無条件付与する
 * ため、same-origin 上に第三者リソースがあると baggage が混入し得る。懸念が残る
 * 場合は本 propagator を CompositePropagator から外し (TraceContext 単体へ戻し)、
 * origination を api / domain-service の認証ミドルウェアへ一本化する (PoC-3)。
 */
export function createOrganizationBaggagePropagator(
  delegate: TextMapPropagator,
  getOrganizationId: () => string | undefined,
): TextMapPropagator {
  return {
    inject(context, carrier, setter) {
      const organizationId = getOrganizationId();
      const injectContext = organizationId
        ? withOrganizationBaggage(context, organizationId)
        : context;
      delegate.inject(injectContext, carrier, setter);
    },
    extract(context, carrier, getter) {
      return delegate.extract(context, carrier, getter);
    },
    fields() {
      return delegate.fields();
    },
  };
}

function withOrganizationBaggage(context: Context, organizationId: string): Context {
  const baggage = (propagation.getBaggage(context) ?? propagation.createBaggage()).setEntry(
    BAGGAGE_ORGANIZATION_ID_KEY,
    { value: organizationId },
  );
  return propagation.setBaggage(context, baggage);
}
